/**
 * Web Speech API wrapper. Lifted from
 * `apps/agent/client/src/lib/voice-input.ts`.
 */

export function isVoiceSupported(): boolean {
  return typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
}

export interface VoiceCallbacks {
  onStart?: () => void
  onInterim?: (text: string) => void
  onFinal?: (text: string) => void
  onError?: (error: string) => void
  onEnd?: () => void
}

interface RawSpeechRecognition {
  continuous: boolean
  interimResults: boolean
  lang: string
  onstart: (() => void) | null
  onresult:
    | ((e: { results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>; resultIndex: number }) => void)
    | null
  onerror: ((e: { error?: string }) => void) | null
  onend: (() => void) | null
  start(): void
  stop(): void
}

export class VoiceRecognizer {
  private recognition: RawSpeechRecognition | null = null
  private finalText = ''
  private interimText = ''
  private active = false
  private callbacks: VoiceCallbacks = {}

  isActive(): boolean {
    return this.active
  }

  start(callbacks: VoiceCallbacks): void {
    if (this.active) {
      return
    }
    const SpeechRecognition =
      (window as unknown as { SpeechRecognition?: { new (): RawSpeechRecognition } }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: { new (): RawSpeechRecognition } }).webkitSpeechRecognition
    if (!SpeechRecognition) {
      callbacks.onError?.('unsupported')
      return
    }

    this.callbacks = callbacks
    this.finalText = ''
    this.interimText = ''
    this.active = true

    const rec = new SpeechRecognition()
    rec.continuous = true
    rec.interimResults = true
    rec.lang = navigator.language || 'en-US'

    rec.onstart = () => {
      callbacks.onStart?.()
    }

    rec.onresult = (event) => {
      let interim = ''
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        if (!result) {
          continue
        }
        const transcript = result[0]?.transcript ?? ''
        if (result.isFinal) {
          this.finalText += transcript
        } else {
          interim += transcript
        }
      }
      this.interimText = interim
      callbacks.onInterim?.((this.finalText + this.interimText).trim())
    }

    rec.onerror = (event) => {
      const error = event?.error ?? 'unknown'
      this.active = false
      this.recognition = null
      callbacks.onError?.(error)
    }

    rec.onend = () => {
      const wasActive = this.active
      this.active = false
      this.recognition = null
      if (wasActive) {
        callbacks.onFinal?.((this.finalText + this.interimText).trim())
      }
      callbacks.onEnd?.()
    }

    this.recognition = rec
    try {
      rec.start()
    } catch (err) {
      this.active = false
      this.recognition = null
      callbacks.onError?.(String((err as Error)?.message ?? err))
    }
  }

  stop(): void {
    if (!this.recognition) {
      return
    }
    try {
      this.recognition.stop()
    } catch {
      /* onend will clean up */
    }
  }
}
